/**
 * Utility functions for handling API errors
 * ==========================================
 *
 * USAGE:
 *   import { parseApiErrorResponse, throwOnApiError } from '@/app/utils/errorUtils';
 *   import ErrorAlert from '@/app/components/ErrorAlert';
 *
 *   // In hooks/components:
 *   const [error, setError] = useState<ApiErrorResponse | null>(null);
 *
 *   try {
 *     const response = await fetch('/api/...');
 *     if (!response.ok) {
 *       const apiError = await parseApiErrorResponse(response);
 *       setError(apiError);
 *       return;
 *     }
 *   } catch (err) {
 *     setError(createNetworkError(err));
 *   }
 *
 *   // In JSX:
 *   {error && <ErrorAlert error={error} onRetry={handleRetry} />}
 */

import {
  ApiErrorResponse,
  ErrorCategory,
  FieldError,
  mapStatusToCategory,
  isApiErrorResponse,
} from '@/app/types/errors';

// Re-export types for convenience
export type { ApiErrorResponse, FieldError };
export { ErrorCategory, isApiErrorResponse };

/**
 * Legacy interface for backward compatibility
 * @deprecated Use ApiErrorResponse instead
 */
export interface ValidationError {
  type: string;
  loc: (string | number)[];
  msg: string;
  input?: unknown;
  ctx?: unknown;
  url?: string;
}

/**
 * Parse API response into standardized ApiErrorResponse
 * Handles both new structured errors and legacy formats
 */
export async function parseApiErrorResponse(response: Response): Promise<ApiErrorResponse> {
  const timestamp = new Date().toISOString();

  try {
    const errorData = await response.json();

    // Already structured error from new backend middleware
    if (isApiErrorResponse(errorData)) {
      return errorData;
    }

    // Legacy: Pydantic validation errors (detail is array)
    if (Array.isArray(errorData.detail)) {
      const fieldErrors: FieldError[] = errorData.detail.map((err: ValidationError) => ({
        field: err.loc ? err.loc.slice(1).join('.') || 'body' : 'unknown',
        message: translateValidationMessage(err.msg),
        error_type: err.type,
        value: err.input !== undefined ? String(err.input).slice(0, 100) : undefined,
      }));

      return {
        error_code: ErrorCategory.VALIDATION,
        message: 'Errori di validazione nei campi inviati',
        technical_details: `${fieldErrors.length} campo/i non valido/i`,
        field_errors: fieldErrors,
        timestamp,
      };
    }

    // Legacy: Simple detail string
    if (typeof errorData.detail === 'string') {
      return {
        error_code: mapStatusToCategory(response.status),
        message: errorData.detail,
        technical_details: `HTTP ${response.status}`,
        timestamp,
      };
    }

    // Legacy: message field
    if (errorData.message) {
      return {
        error_code: mapStatusToCategory(response.status),
        message: errorData.message,
        technical_details: errorData.error || `HTTP ${response.status}`,
        timestamp,
      };
    }

    // Fallback
    return {
      error_code: mapStatusToCategory(response.status),
      message: `Errore HTTP ${response.status}: ${response.statusText}`,
      technical_details: JSON.stringify(errorData).slice(0, 200),
      timestamp,
    };
  } catch {
    // Failed to parse JSON
    return {
      error_code: mapStatusToCategory(response.status),
      message: `Errore HTTP ${response.status}: ${response.statusText}`,
      timestamp,
    };
  }
}

/**
 * ⛔ Un 403 su una sezione FACOLTATIVA (DEC-08: classifica provvisoria,
 * iscritti, presenze, costi, montepremi, cassa) non è un errore applicativo:
 * è il verdetto del backend su chi può leggere quel dato. Chi chiama lo
 * distingue da un guasto vero per decidere se nascondere la sezione o
 * mostrarne il motivo in forma neutra — mai un banner d'errore generico, e
 * mai una regola di chi-può-vedere-cosa decisa nel browser: il messaggio è
 * sempre quello del backend (`error.message`).
 */
export function isAuthorizationError(
  error: ApiErrorResponse | Error | string | null | undefined
): boolean {
  if (!error || typeof error === 'string') return false;
  if (error instanceof Error) {
    const apiError = (error as Error & { apiError?: ApiErrorResponse }).apiError;
    return apiError?.error_code === ErrorCategory.AUTHORIZATION;
  }
  return error.error_code === ErrorCategory.AUTHORIZATION;
}

/**
 * Esito della lettura di una sezione FACOLTATIVA di una pagina:
 * - `data`: il contenuto, se la lettura è riuscita;
 * - `restricted`: true se il backend ha risposto 403 — chi chiama decide se
 *   nascondere la sezione o mostrare `reason` (il motivo del backend, in
 *   forma neutra, mai dedotto da ruoli o privilegi letti nel browser);
 * - `error`: un `ApiErrorResponse` vero (non un 403), da trattare come guasto.
 */
export interface OptionalSectionResult<T> {
  data: T | null;
  restricted: boolean;
  reason: string | null;
  error: ApiErrorResponse | null;
}

/**
 * Legge la risposta di una sezione FACOLTATIVA: un 403 non interrompe la
 * pagina, diventa `restricted` col motivo del backend.
 */
export async function readOptionalSection<T>(response: Response): Promise<OptionalSectionResult<T>> {
  if (response.ok) {
    return { data: (await response.json()) as T, restricted: false, reason: null, error: null };
  }
  const apiError = await parseApiErrorResponse(response);
  if (isAuthorizationError(apiError)) {
    return { data: null, restricted: true, reason: apiError.message, error: null };
  }
  return { data: null, restricted: false, reason: null, error: apiError };
}

/**
 * Create network error (for catch blocks)
 */
export function createNetworkError(error: unknown): ApiErrorResponse {
  const message = error instanceof Error ? error.message : 'Errore di rete sconosciuto';

  let category = ErrorCategory.NETWORK;
  if (message.toLowerCase().includes('timeout')) {
    category = ErrorCategory.TIMEOUT;
  } else if (message.toLowerCase().includes('abort')) {
    category = ErrorCategory.NETWORK;
  }

  return {
    error_code: category,
    message: 'Errore di connessione al server',
    technical_details: message,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Throw structured error from API response
 * Use in hooks when you want to throw instead of return
 */
export async function throwOnApiError(response: Response, operation: string): Promise<void> {
  if (!response.ok) {
    const errorData = await parseApiErrorResponse(response);

    // Create Error with attached apiError for ErrorAlert to parse
    const error = new Error(errorData.message) as Error & { apiError: ApiErrorResponse };
    error.apiError = {
      ...errorData,
      technical_details: errorData.technical_details || `Errore durante: ${operation}`,
    };
    throw error;
  }
}

/**
 * Translate common Pydantic validation messages to Italian
 */
function translateValidationMessage(msg: string): string {
  const translations: Record<string, string> = {
    'field required': 'campo obbligatorio',
    'Field required': 'Campo obbligatorio',
    'value is not a valid email address': 'indirizzo email non valido',
    'Input should be a valid email': 'formato email non valido',
    'Input should be a valid datetime': 'formato data non valido',
    'Input should be a valid integer': 'deve essere un numero intero',
    'Input should be a valid number': 'deve essere un numero',
    'Input should be a valid boolean': 'deve essere vero o falso',
    'String should have at least': 'deve avere almeno',
    'String should have at most': 'deve avere al massimo',
    'characters': 'caratteri',
    'Value error, ': '',
  };

  let translated = msg;
  for (const [en, it] of Object.entries(translations)) {
    translated = translated.replace(en, it);
  }
  return translated;
}

// ============================================
// LEGACY FUNCTIONS (for backward compatibility)
// ============================================

/**
 * @deprecated Use parseApiErrorResponse() instead
 */
export function parseApiError(errorData: unknown, defaultMessage: string = 'Errore sconosciuto'): string {
  if (!errorData) return defaultMessage;
  if (typeof errorData === 'string') return errorData;

  const data = errorData as Record<string, unknown>;

  if (data.message && typeof data.message === 'string') {
    return data.message;
  }

  if (data.detail) {
    if (typeof data.detail === 'string') return data.detail;
    if (Array.isArray(data.detail)) {
      return data.detail.map((e: ValidationError) => e.msg).join(', ');
    }
  }

  return defaultMessage;
}

/**
 * @deprecated Use throwOnApiError() instead
 */
export async function handleApiError(response: Response, defaultMessage: string = 'Errore API'): Promise<never> {
  const errorData = await parseApiErrorResponse(response);
  throw new Error(errorData.message || defaultMessage);
}
